import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { loadCastFramework } from '../casting/loadCastFramework';

export interface UseChromecastParams {
  videoRef: RefObject<HTMLVideoElement | null>;
  streamUrl?: string | null;
  title: string;
  poster?: string;
  isHls: boolean;
  isLive: boolean;
  enabled: boolean;
  onRemotePlayingChange?: (playing: boolean) => void;
}

export interface UseChromecastResult {
  castAvailable: boolean;
  castConnected: boolean;
  castConnecting: boolean;
  castError: string | null;
  requestCast: () => Promise<void>;
  stopCast: () => void;
}

function contentTypeForStream(isHls: boolean): string {
  return isHls ? 'application/vnd.apple.mpegurl' : 'video/mp4';
}

/* eslint-disable @typescript-eslint/no-explicit-any -- Cast Web Sender uses large global APIs. */
function win(): any {
  return typeof window !== 'undefined' ? window : undefined;
}

export function useChromecast(params: UseChromecastParams): UseChromecastResult {
  const { videoRef, streamUrl, title, poster, isHls, isLive, enabled, onRemotePlayingChange } = params;

  const [castAvailable, setCastAvailable] = useState(false);
  const [castConnected, setCastConnected] = useState(false);
  const [castConnecting, setCastConnecting] = useState(false);
  const [castError, setCastError] = useState<string | null>(null);

  const remoteControllerRef = useRef<any>(null);
  const remoteListenerRef = useRef<(() => void) | null>(null);
  const ctxListenerRef = useRef<(() => void) | null>(null);
  const lastLoadedUrlRef = useRef<string>('');

  const detachRemoteListeners = useCallback(() => {
    const c = remoteControllerRef.current;
    const w = win();
    if (c && remoteListenerRef.current && w?.cast?.framework?.RemotePlayerEventType) {
      try {
        c.removeEventListener(w.cast.framework.RemotePlayerEventType.IS_PAUSED_CHANGED, remoteListenerRef.current);
      } catch {
        /* ignore */
      }
    }
    remoteListenerRef.current = null;
    remoteControllerRef.current = null;
  }, []);

  const attachRemoteListeners = useCallback(() => {
    detachRemoteListeners();
    const w = win();
    const CF = w?.cast?.framework;
    if (!CF) return;
    try {
      const remotePlayer = new CF.RemotePlayer();
      const remoteController = new CF.RemotePlayerController(remotePlayer);
      remoteControllerRef.current = remoteController;
      const onPausedChanged = () => {
        try {
          onRemotePlayingChange?.(!remoteController.isPaused);
        } catch {
          /* ignore */
        }
      };
      remoteListenerRef.current = onPausedChanged;
      remoteController.addEventListener(CF.RemotePlayerEventType.IS_PAUSED_CHANGED, onPausedChanged);
    } catch {
      /* ignore */
    }
  }, [detachRemoteListeners, onRemotePlayingChange]);

  const loadMediaOnSession = useCallback(async () => {
    const w = win();
    const url = streamUrl?.trim();
    if (!url) throw new Error('No stream URL to cast.');

    const ctx = w?.cast?.framework?.CastContext?.getInstance?.();
    const session = ctx?.getCurrentSession?.();
    if (!session) throw new Error('No Cast session.');

    const chromeCast = w.chrome?.cast;
    if (!chromeCast?.media) throw new Error('Cast media API missing.');

    const mediaInfo = new chromeCast.media.MediaInfo(url, contentTypeForStream(isHls));
    mediaInfo.streamType = isLive ? chromeCast.media.StreamType.LIVE : chromeCast.media.StreamType.BUFFERED;

    const meta = new chromeCast.media.GenericMediaMetadata();
    meta.metadataType = chromeCast.media.MetadataType.GENERIC;
    meta.title = title || 'Video';
    if (poster) {
      meta.images = [new chromeCast.media.Image(poster, 480, 270)];
    }
    mediaInfo.metadata = meta;

    const request = new chromeCast.media.LoadRequest(mediaInfo);
    await session.loadMedia(request);
    lastLoadedUrlRef.current = url;
    videoRef.current?.pause();
    onRemotePlayingChange?.(true);
  }, [streamUrl, isHls, isLive, title, poster, videoRef, onRemotePlayingChange]);

  const loadMediaRef = useRef(loadMediaOnSession);
  loadMediaRef.current = loadMediaOnSession;

  /* Load Cast framework, configure receiver, subscribe to connection state. */
  useEffect(() => {
    if (!enabled) {
      try {
        win()?.cast?.framework?.CastContext?.getInstance?.()?.getCurrentSession?.()?.endSession?.(true);
      } catch {
        /* ignore */
      }
      setCastAvailable(false);
      setCastConnected(false);
      setCastError(null);
      lastLoadedUrlRef.current = '';
      detachRemoteListeners();
      return;
    }

    let cancelled = false;
    let ctx: any;
    const CF = () => win()?.cast?.framework;

    const setup = async () => {
      const ok = await loadCastFramework();
      if (cancelled || !ok) {
        if (!cancelled) setCastAvailable(false);
        return;
      }
      const w = win();
      ctx = w?.cast?.framework?.CastContext?.getInstance?.();
      if (!ctx || cancelled) return;

      try {
        ctx.setOptions({
          receiverApplicationId: w.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
          autoJoinPolicy: w.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
          resumeSavedSession: false,
        });
      } catch {
        if (!cancelled) setCastAvailable(false);
        return;
      }

      const framework = CF();
      if (!framework || cancelled) return;

      const onCtx = () => {
        if (cancelled) return;
        try {
          const state = ctx.getCastState?.();
          const { NO_DEVICES_AVAILABLE, CONNECTED } = framework.CastState;
          setCastAvailable(state !== NO_DEVICES_AVAILABLE);
          const connected = state === CONNECTED;
          setCastConnected(connected);
          if (connected) attachRemoteListeners();
          else {
            detachRemoteListeners();
            lastLoadedUrlRef.current = '';
          }
        } catch {
          setCastAvailable(false);
        }
      };

      ctxListenerRef.current = onCtx;
      ctx.addEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, onCtx);
      onCtx();
    };

    void setup();

    return () => {
      cancelled = true;
      try {
        if (ctx && ctxListenerRef.current) {
          const framework = CF();
          if (framework) {
            ctx.removeEventListener(framework.CastContextEventType.CAST_STATE_CHANGED, ctxListenerRef.current);
          }
        }
      } catch {
        /* ignore */
      }
      ctxListenerRef.current = null;
      detachRemoteListeners();
    };
  }, [enabled, attachRemoteListeners, detachRemoteListeners]);

  /* When connected, push the active stream (and again when URL/metadata change). */
  useEffect(() => {
    if (!enabled || !castConnected) return;
    const url = streamUrl?.trim();
    if (!url) return;

    let cancelled = false;
    void (async () => {
      try {
        await loadMediaRef.current();
        if (!cancelled) setCastError(null);
      } catch (e) {
        if (!cancelled) {
          setCastError(e instanceof Error ? e.message : 'Cast load failed');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, castConnected, streamUrl, title, poster, isHls, isLive]);

  const requestCast = useCallback(async () => {
    setCastError(null);
    const w = win();
    const ctx = w?.cast?.framework?.CastContext?.getInstance?.();
    if (!ctx) {
      setCastError('Cast is not available in this browser.');
      return;
    }

    setCastConnecting(true);
    try {
      const { CONNECTED } = w.cast.framework.CastState;
      if (ctx.getCastState?.() !== CONNECTED) {
        await ctx.requestSession();
      }
      await loadMediaOnSession();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Cast failed';
      if (!/cancel|abort/i.test(msg)) {
        setCastError(msg);
      }
    } finally {
      setCastConnecting(false);
    }
  }, [loadMediaOnSession]);

  const stopCast = useCallback(() => {
    try {
      const w = win();
      const ctx = w?.cast?.framework?.CastContext?.getInstance?.();
      ctx?.getCurrentSession?.()?.endSession?.(true);
    } catch {
      /* ignore */
    }
    lastLoadedUrlRef.current = '';
    setCastError(null);
  }, []);

  return {
    castAvailable,
    castConnected,
    castConnecting,
    castError,
    requestCast,
    stopCast,
  };
}
